import { Injectable, inject } from '@angular/core';
import { InsertionPlanChoiceService } from 'src/app/collections/inspection-plan-assistant/insertion-plan-choice.service';
import { HttpClient } from '@angular/common/http';
import { StorageService, User, SESSION_STORAGE, Util } from '@phdsoft/common';
import { AssetManager } from 'src/app/asset-manager/asset-manager';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';
import { AutomaticPoints } from '../_models/AutomaticPoints';
import { GeneralSettings } from '../_models/GeneralSettings';
import { InspectionRegion } from '../_models/InspectionRegion';
import { AreaSettings } from '../_models/AreaSettings';
import { IAutomaticPlanTypeSelectorData } from '../automatic-plan-type-selector/automatic-plan-type-selector.component';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class AutomaticPlanAreaWizardService {
  /** Where the day is in several open plans, which one the new points go to. */
  private readonly insertionPlanChoice = inject(InsertionPlanChoiceService);

  constructor(
    private readonly http: HttpClient,
    private readonly storageService: StorageService,
    private readonly assetManager: AssetManager
  ) { }

  /**
   * Execute the automatic plan area generation with default configuration
   * @param selectorData The automatic plan type selector data
   * @returns Promise with the response data
   */
  async executeWithDefaultConfig(
    inspectionTypeId: string,
    inspectionMethodId: string, 
    inspectionTypeName: string,
    checkLists?: any[]
  ) {
    const autoPoints = this.createDefaultConfiguration(
      inspectionTypeId,
      inspectionMethodId,
      inspectionTypeName,
      checkLists
    );
    return this.executeAutomaticPoints(autoPoints);
  }

  /**
   * Creates a default configuration for automatic points
   */
  private createDefaultConfiguration(
    inspectionTypeId: string,
    inspectionMethodId: string, 
    inspectionTypeName: string,
    checkLists?: any[]
  ): AutomaticPoints {
    const assetId = this.assetManager.asset.assetInfo.AssetId;
    const automaticPoints = new AutomaticPoints();
    
    // Basic configuration
    automaticPoints.Id = null;
    automaticPoints.AssetId = assetId;
    automaticPoints.CollectionId = null;
    automaticPoints.Label = "AutomaticPoints";
    automaticPoints.InspectionTypeId = inspectionTypeId;
    automaticPoints.InspectionMethodId = inspectionMethodId;
    automaticPoints.InspectionTypeName = inspectionTypeName;
    automaticPoints.InspectionTypeAlias = this.assetManager.listInpectionType?.find(t => t.id === inspectionTypeId)?.alias;
    automaticPoints.CheckLists = checkLists || [];
    automaticPoints.Date = this.assetManager.referenceDate;

    // Add general settings
    automaticPoints.GeneralSettings = new GeneralSettings();
    automaticPoints.GeneralSettings.CurrentPage = true;
    automaticPoints.GeneralSettings.AllPages = false;
    automaticPoints.GeneralSettings.BulkheadsNoStifffners = true;
    automaticPoints.GeneralSettings.DeleteOldAutomaticPoints = false;
    automaticPoints.GeneralSettings.Date = moment(this.assetManager.referenceDate).format("yyyy-MM-DD");

    // Get visible regions and sections
    automaticPoints.GeneralSettings.Regions = this.getVisibleRegions();

    // Configure area settings
    automaticPoints.AreaSettings = new AreaSettings();
    automaticPoints.AreaSettings.AreaForCriticalAreas = true;

    // Set other settings to null as they are not used in area wizard
    automaticPoints.PlatesSettings = null;
    automaticPoints.StiffenersSettings = null;
    automaticPoints.FacePlatesSettings = null;
    automaticPoints.BracketSettings = null;

    return automaticPoints;
  }

  /**
   * Get visible regions and sections from the asset manager
   */
  private getVisibleRegions() {
    const regions = [];
    const visibleRegions = this.assetManager.asset.GetStructureVisibleRegions();
    
    for (const region of visibleRegions) {
      if (!region.Guid) continue;
      
      const sections = region.sections
        .filter(section => section.visible)
        .map(section => ({
          SectionId: section.Guid, 
          SectionName: section.Name,
          SectionChecked: true
        }));
      
      const sectionIds = sections.map(section => section.SectionId);
      
      regions.push({
        RegionId: region.Guid,
        RegionName: region.Name,
        RegionChecked: true,
        SectionIds: sectionIds,
        Sections: sections
      });
    }
    
    return regions;
  }

  /**
   * Execute automatic points generation
   */
  private async executeAutomaticPoints(autoPoints: AutomaticPoints) {
    // Set user and collection ID
    const user = await this.storageService.getItem<any>(SESSION_STORAGE.USER) as User;
    autoPoints.UserId = user.id;
    autoPoints.CollectionId = uuidv4().replace(/-/g, '') as string;
    if (!(await this.insertionPlanChoice.prepareBotRequest(autoPoints))) return null;

    // Make the API request
    const response = await this.http.post<any>(
      environment.API_INSPEC + 'UtmPoints/signalrutmautomaticinspectionpoints',
      autoPoints
    ).toPromise();

    return response;
  }
} 